import { Fragment, useEffect, useMemo, useState } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { BellIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { MenuIcon } from 'lucide-react';
import { Form, NavLink, useLocation } from 'react-router';
import { twMerge } from 'tailwind-merge';

import logoBlack from '~/assets/logo/Logo1_black.svg';
import logoWhite from '~/assets/logo/Logo1_white.svg';
import { useCartContext } from '~/components/Cart/CartDrawerContext';
import { paths } from '~/constants/paths';
import { orderListQuery } from '~/hooks/api/orderListQuery';
import { useFeatureFlagState } from '~/posthog/useFeatureFlagState';
import {
  type Auth0Metadata,
  CONDUIT_EMPLOYEE_ROLE,
  SUPPLIER_ROLE,
  SYSTEM_ADMINISTRATOR_ROLE,
  type Session,
  type UserRole,
} from '~/types/auth';
import { getUserFromIdToken } from '~/utils/profile';

import ConduitHealthLoader from './Loader/ConduitHealthLoader';
import { useGlobalLoaderStore } from './Loader/useGlobalLoader';
import { Notifications } from './Notifications';

type NavbarProps = {
  user: Session | null;
  userRole: UserRole;
  userName: string;
  isImpersonating?: boolean;
};

export function Navbar({ user, userRole, userName, isImpersonating = false }: NavbarProps) {
  const isGetOrdersFetching = useIsFetching({ ...orderListQuery() });
  const adminAccess = useFeatureFlagState('admin-section-access');

  // const [theme, setTheme] = React.useState<'light' | 'dark'>('light');
  const theme = 'light';

  // Impersonation Logic
  const [impersonatedUser, setImpersonatedUser] = useState('');

  const sysAdminImpersonating = Boolean(userRole === SYSTEM_ADMINISTRATOR_ROLE && impersonatedUser);

  const { email, picture } = getUserFromIdToken(user?.idToken);

  // Navigation
  type NavItem = {
    name: string;
    href: string;
    restrictedVisibility?: boolean;
    disabled?: boolean;
  };
  type Navigation = {
    [key in Auth0Metadata['role']]: NavItem[];
  };

  const userNavLink: Navigation = {
    clinician: [
      { name: 'Marketplace', href: paths.marketplaceDiscover },
      { name: 'Orders', href: paths.orders },
    ],
    marketplace_user: [
      { name: 'Marketplace', href: paths.marketplaceDiscover },
      { name: 'Orders', href: paths.orders },
      {
        name: 'Patients',
        href: paths.patients,
        restrictedVisibility: true,
      },
    ],
    supplier: [{ name: 'Orders', href: paths.orders }],
    system_administrator: [
      {
        name: 'Orders',
        href: paths.orders,
        restrictedVisibility: sysAdminImpersonating,
      },
    ],
    conduit_employee: [
      { name: 'Orders', href: paths.orders },
      {
        name: 'Admin',
        href: paths.admin,
        disabled: adminAccess !== 'enabled',
      },
      {
        name: 'Patients',
        href: paths.patients,
        restrictedVisibility: true,
      },
    ],
  };

  const navLinks = userNavLink[userRole];
  const userNavigation = [{ name: 'Your Profile', href: paths.profile }];

  // Colors and Themes
  type Theme = {
    logo: string;
    'text-color': string;
    'text-hover-color': string;
    'border-color': string;
    'border-hover-color': string;
    'bg-color': 'bg-white' | 'bg-transparent' | 'bg-[#032422]';
    'login-button-variant': 'primary' | 'outline' | 'outline-transparent';
  };

  const lightTheme: Theme = useMemo(() => {
    return {
      logo: logoBlack,
      'text-color': 'text-black',
      'text-hover-color': 'hover:text-gray-700',
      'border-color': 'border-black',
      'border-hover-color': 'hover:border-gray-300',
      'bg-color': 'bg-white',
      'login-button-variant': 'primary',
    };
  }, []);

  const darkTheme: Theme = useMemo(() => {
    return {
      logo: logoWhite,
      'text-color': 'text-white',
      'text-hover-color': 'hover:text-gray-300',
      'border-color': 'border-white',
      'border-hover-color': 'hover:border-gray-300',
      'bg-color': 'bg-[#032422]',
      'login-button-variant': 'outline-transparent',
    };
  }, []);

  const colorScheme = useMemo(() => {
    return {
      light: lightTheme,
      dark: darkTheme,
    };
  }, [darkTheme, lightTheme]);

  const loaderVisible = useGlobalLoaderStore((s) => s.loaderVisible);

  const { cart, setCartOpen } = useCartContext();

  const addingProduct = useIsMutating({ mutationKey: ['add-cart-product'] }) > 0;
  const removingProduct = useIsMutating({ mutationKey: ['cart-remove-item'] }) > 0;

  const optimisticCartSize =
    (cart?.items?.length ?? 0) + (addingProduct ? 1 : 0) + (removingProduct ? -1 : 0);

  const showShoppingBag = ![CONDUIT_EMPLOYEE_ROLE, SUPPLIER_ROLE].includes(userRole);

  const location = useLocation();
  const [bgColor, setBgColor] = useState<'bg-white' | 'bg-transparent' | 'bg-[#032422]'>(
    'bg-transparent'
  );

  const handleImpersonation = () => {
    // isImpersonating checks if an impersonation cookie is set
    if (!isImpersonating) {
      setImpersonatedUser(prompt('Who you would like to impersonate') || '');
    } else {
      setImpersonatedUser('');
    }
  };

  // Location helpers
  const isAccountCreationPage = location.pathname.includes('/create-account');
  const isIndexPage = location.pathname === '/';
  const isAppPage = location.pathname.startsWith(paths.app);

  if (isAppPage && bgColor !== 'bg-white') {
    setBgColor('bg-white');
  }

  useEffect(() => {
    const rootDiv = document.getElementById('rootDiv');

    function handleScroll() {
      if (rootDiv && rootDiv.scrollTop > 0) {
        setBgColor(colorScheme[theme]['bg-color']);
      } else {
        setBgColor('bg-transparent');
      }
    }

    rootDiv?.scrollTo(0, 0);

    if (!isAppPage) {
      rootDiv?.addEventListener('scroll', handleScroll);
    }

    return () => {
      rootDiv?.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname, theme, colorScheme, isAppPage]);

  if (isAccountCreationPage) return null;

  return (
    <>
      <Disclosure as="nav" className={twMerge('sticky top-0 z-50 bg-white')}>
        {({ open }) => (
          <>
            <div className={`${!isIndexPage ? 'border-b' : null} ${open ? 'bg-white' : bgColor}`}>
              <div
                className={`mx-auto flex h-16 justify-between px-6 tablet:px-8 desktop:px-12 ${
                  location.pathname.startsWith('/app/checkout')
                    ? 'max-w-[2500px]'
                    : 'max-w-[1440px]'
                }`}
              >
                {/* Desktop Nav */}
                <div className="flex w-full items-center justify-between">
                  {/*  Start - Logo */}
                  <div className="flex items-center">
                    <img
                      src={open ? logoBlack : colorScheme[theme]['logo']}
                      className="pointer-events-none h-auto max-w-[240px] select-none"
                      alt="ConduitHealth Logo"
                      draggable={false}
                      style={
                        loaderVisible
                          ? {
                              animationDuration: '1s',
                              animationIterationCount: 'infinite',
                            }
                          : {}
                      }
                    />
                    <span className="mt-[7px] ml-[-18px] text-lg font-medium italic">
                      {userRole === SUPPLIER_ROLE ? 'Supplier' : ''}
                      {userRole === CONDUIT_EMPLOYEE_ROLE ? 'Clinical' : ''}
                    </span>
                  </div>

                  {/* Middle - Links hidden in small displays */}
                  <div className="hidden h-full items-center laptop:flex laptop:flex-1">
                    <div className="ml-10 hidden h-full items-center gap-10 laptop:flex">
                      {navLinks.map((item) => {
                        const { href, name, restrictedVisibility, disabled } = item;
                        if (restrictedVisibility !== undefined && !restrictedVisibility) {
                          return null;
                        }
                        if (disabled) {
                          return (
                            <span
                              key={href}
                              aria-disabled="true"
                              className={twMerge(
                                'inline-flex h-full cursor-not-allowed items-center border-b-[3px] border-transparent text-sm font-normal opacity-40',
                                colorScheme[theme]['text-color']
                              )}
                            >
                              {name}
                            </span>
                          );
                        }
                        return (
                          <NavLink
                            key={href}
                            to={href}
                            className={({ isActive }) =>
                              twMerge(
                                `inline-flex h-full items-center border-b-[3px] text-sm font-normal ${colorScheme[theme]['text-color']}`,
                                // make active link bold and add border
                                isActive
                                  ? `${colorScheme[theme]['border-color']} font-semibold`
                                  : `border-transparent ${colorScheme[theme]['border-hover-color']} ${colorScheme[theme]['text-hover-color']}`
                              )
                            }
                          >
                            {name}
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>

                  {/* End - User Controls and Mobile Menu Button*/}
                  <div className="">
                    {/* Mobile Nav */}
                    <div className="flex items-center laptop:hidden">
                      {/* Mobile menu button */}
                      <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:text-gray-500">
                        <span className="sr-only">Open main menu</span>
                        <MenuIcon className="block h-6 w-6" aria-hidden="true" />
                      </Disclosure.Button>
                    </div>

                    {user ? (
                      <div className="hidden items-center space-x-4 laptop:flex">
                        {isGetOrdersFetching ? <ConduitHealthLoader size="small" /> : null}
                        {/* Profile dropdown */}
                        <Menu as="div" className="relative">
                          <div>
                            <Menu.Button className="flex rounded-full bg-white text-sm focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none">
                              <span className="sr-only">Open user menu</span>
                              <img
                                className="h-6 w-6 rounded-full select-none"
                                src={picture}
                                alt={userName}
                                draggable={false}
                              />
                            </Menu.Button>
                          </div>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-200"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="ring-opacity-5 absolute right-0 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black focus:outline-none">
                              {/* User navigation */}
                              {userNavigation.map((item) => (
                                <Menu.Item key={item.href}>
                                  {({ active }) => (
                                    <NavLink
                                      to={item.href}
                                      className={twMerge(
                                        active ? 'bg-gray-100' : '',
                                        'block px-4 py-2 text-sm text-gray-700'
                                      )}
                                    >
                                      {item.name}
                                    </NavLink>
                                  )}
                                </Menu.Item>
                              ))}

                              {/* Sys Admin impersonation */}
                              {user &&
                                userRole &&
                                (userRole === SYSTEM_ADMINISTRATOR_ROLE || isImpersonating) && (
                                  <Form
                                    method="post"
                                    action="/auth/impersonate"
                                    className="block text-sm text-gray-700"
                                  >
                                    <input
                                      type="hidden"
                                      name="impersonatedUserId"
                                      value={impersonatedUser}
                                      readOnly
                                    />
                                    <button
                                      type="submit"
                                      className="block w-full px-4 py-2 text-left"
                                      onClick={handleImpersonation}
                                    >
                                      {isImpersonating ? 'Stop impersonating' : 'Impersonate'}
                                    </button>
                                  </Form>
                                )}

                              {/* Logout form */}
                              <Menu.Item>
                                {({ active }) => (
                                  <Form
                                    method="post"
                                    action={paths.logout}
                                    className={twMerge(
                                      active ? 'bg-gray-100' : '',
                                      'block text-sm text-gray-700'
                                    )}
                                  >
                                    <button
                                      type="submit"
                                      className="pointer block w-full px-4 py-2 text-left"
                                    >
                                      Sign out
                                    </button>
                                  </Form>
                                )}
                              </Menu.Item>
                            </Menu.Items>
                          </Transition>
                        </Menu>
                        <Notifications />
                        {showShoppingBag && (
                          <button
                            type="button"
                            className="relative inline-block rounded-full bg-transparent p-1 text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none"
                            data-testid="open-cart"
                            onClick={() => setCartOpen(true)}
                          >
                            <span className="sr-only">View cart</span>
                            <ShoppingBagIcon className="h-6 w-6" aria-hidden="true" />
                            {optimisticCartSize > 0 && (
                              <span
                                data-testid="cartHasItems"
                                className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-teal-700 ring-2 ring-white"
                              />
                            )}
                          </button>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Nav Dropdown */}
            <Transition
              enter="transition duration-100 ease-out transform"
              enterFrom="opacity-0 translate-y-[-10px]"
              enterTo="opacity-100 translate-y-0"
              leave="transition duration-75 ease-out transform"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-[-10px]"
            >
              <Disclosure.Panel className="bg-white laptop:hidden">
                {({ close }) => (
                  <div className="px-6 tablet:px-8">
                    <div className="space-y-1">
                      {navLinks.map((item) => {
                        const { href, name, restrictedVisibility, disabled } = item;
                        if (restrictedVisibility !== undefined && !restrictedVisibility) {
                          return null;
                        }
                        if (disabled) {
                          return (
                            <span
                              key={href}
                              aria-disabled="true"
                              className="block cursor-not-allowed px-4 py-2 text-base font-medium text-gray-400"
                            >
                              {name}
                            </span>
                          );
                        }
                        return (
                          <Disclosure.Button
                            key={href}
                            as={NavLink}
                            to={href}
                            className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                          >
                            {name}
                          </Disclosure.Button>
                        );
                      })}
                    </div>
                    <div className="border-t border-gray-200 pt-4 pb-3">
                      <div className="flex items-center px-4">
                        <div className="shrink-0">
                          <img
                            className="h-10 w-10 rounded-full select-none"
                            src={picture}
                            alt={userName}
                            draggable={false}
                          />
                        </div>
                        <div className="ml-3">
                          <div className="text-base font-medium text-gray-800">{userName}</div>
                          <div className="text-sm font-medium text-gray-500">{email}</div>
                        </div>
                        <button
                          type="button"
                          className="ml-auto shrink-0 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none"
                        >
                          <span className="sr-only">View notifications</span>
                          <BellIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>

                      <div className="mt-3 space-y-1">
                        {userNavigation.map((item) => (
                          <Disclosure.Button
                            key={item.href}
                            as={NavLink}
                            to={item.href}
                            className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                          >
                            {item.name}
                          </Disclosure.Button>
                        ))}

                        <Form
                          method="post"
                          action={paths.logout}
                          className="block text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        >
                          <button
                            type="submit"
                            className="block w-full px-4 py-2 text-left"
                            onClick={() => setTimeout(close, 100)}
                          >
                            Sign out
                          </button>
                        </Form>
                      </div>
                    </div>
                  </div>
                )}
              </Disclosure.Panel>
            </Transition>
          </>
        )}
      </Disclosure>

      {isImpersonating && (
        <div className="h-6 bg-green-400 text-center text-white">
          !!!WARNING!!! You are impersonating {userName}
        </div>
      )}
    </>
  );
}
